/**
 * Аватарки со сквозным шифрованием.
 *
 * Задача неочевидная: ключ чата тут не подходит, потому что аватарка одна
 * на человека, а чатов у него несколько. Поэтому у каждого свой ключ аватарки,
 * и владелец запечатывает его публичным ключом каждого, с кем состоит в общем
 * чате. Сервер хранит зашифрованную картинку и набор запечатанных ключей —
 * открыть не может ни то, ни другое.
 *
 * Появился новый общий чат — владелец допечатывает ключ его участникам при
 * ближайшей синхронизации, так что набор получателей чинится сам.
 */
import { fromBase64, toBase64 } from './bytes.js';

/**
 * 256 пикселей и WebP.
 *
 * Аватарка показывается кружком в 42 пикселя, так что 256 хватает и на экраны
 * с тройной плотностью. Уменьшение до загрузки принципиально: оригинал с
 * телефона весит мегабайты, а тут каждый байт едет в SQLite и обратно.
 */
const MAX_SIZE = 256;
const QUALITY = 0.82;

/** Сжимает и кадрирует картинку по центру в квадрат. */
export async function prepareImage(file: File): Promise<Uint8Array> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Чаще всего сюда приводит HEIC с айфона в браузере, который его не
    // понимает. Системное сообщение о неудачном декодировании ничего
    // не объясняет, поэтому подсказываем, что делать.
    throw new Error(
      'Не получилось прочитать это изображение. Попробуй другой файл — ' +
        'надёжнее всего JPEG или PNG.',
    );
  }

  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - side) / 2;
    const sy = (bitmap.height - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = MAX_SIZE;
    canvas.height = MAX_SIZE;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('не удалось подготовить изображение');
    context.drawImage(bitmap, sx, sy, side, side, 0, 0, MAX_SIZE, MAX_SIZE);

    const blob = await new Promise<Blob | null>((resolve) =>
      // WebP заметно меньше JPEG при том же качестве и поддерживается везде,
      // где работает всё остальное в этом приложении.
      canvas.toBlob(resolve, 'image/webp', QUALITY),
    );
    if (!blob) throw new Error('не удалось сжать изображение');

    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    bitmap.close();
  }
}

export interface EncryptedAvatar {
  /** nonce ‖ шифротекст, base64. */
  blob: string;
  key: Uint8Array;
}

export async function encryptAvatar(image: Uint8Array): Promise<EncryptedAvatar> {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, [
    'encrypt',
  ]);

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, image as BufferSource),
  );

  const blob = new Uint8Array(iv.length + ciphertext.length);
  blob.set(iv);
  blob.set(ciphertext, iv.length);

  return { blob: toBase64(blob), key: raw };
}

/** Возвращает null, если ключ не тот или данные повреждены. */
export async function decryptAvatar(blobB64: string, rawKey: Uint8Array): Promise<Blob | null> {
  try {
    const blob = fromBase64(blobB64);
    const key = await crypto.subtle.importKey('raw', rawKey as BufferSource, 'AES-GCM', false, [
      'decrypt',
    ]);

    const image = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: blob.subarray(0, 12) as BufferSource },
      key,
      blob.subarray(12) as BufferSource,
    );
    return new Blob([image], { type: 'image/webp' });
  } catch {
    return null;
  }
}

/** Data URL, чтобы аватарку можно было положить в IndexedDB и пережить перезапуск. */
export function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
